import React, { useEffect, useState } from 'react';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import "../../styles/components/_home.scss";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
} from 'chart.js';
import { DollarSign, Users, TrendingUp, Eye, Target, BarChart2, Wallet } from 'lucide-react';
import EnterpriseProvider from '../../infra/provider/enterpriseProvider';
import MetricsProvider from '../../infra/provider/metricsProvider';
import { SaleMetrics } from '../../infra/repository/metricsRepository';
import Enterprise from '../../domain/entities/enterprise';


ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
);

type Period = 'year' | 'month' | 'week';

function Home() {
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('year');
  const [selectedEnterprise, setSelectedEnterprise] = useState<string>('all');
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [vgvPercentage, setVgvPercentage] = useState(62);
  const [metrics, setMetrics] = useState({
    vendas: 0,
    empreendimentos: 0,
    corretores: 0,
    visitas: 0,
    leads: 0,
    bouncerate: 0,
    valor: 0
  });
  const [saleMetrics, setSaleMetrics] = useState<SaleMetrics | null>(null);

  const salesValueData = {
    year: {
      labels: saleMetrics?.metrics.detailedMetrics.periodDetails.map(detail => detail.label) || [],
      data: saleMetrics?.metrics.detailedMetrics.periodDetails.map(detail => detail.soldValue) || [],
    },
    month: {
      labels: saleMetrics?.metrics.detailedMetrics.periodDetails.map(detail => detail.label) || [],
      data: saleMetrics?.metrics.detailedMetrics.periodDetails.map(detail => detail.soldValue) || [],
    },
    week: {
      labels: saleMetrics?.metrics.detailedMetrics.periodDetails.map(detail => detail.label) || [],
      data: saleMetrics?.metrics.detailedMetrics.periodDetails.map(detail => detail.soldValue) || [],
    }
  };

  const salesQuantityData = {
    year: {
      labels: saleMetrics?.metrics.detailedMetrics.periodDetails.map(detail => detail.label) || [],
      data: saleMetrics?.metrics.detailedMetrics.periodDetails.map(detail => detail.soldUnits) || [],
    },
    month: {
      labels: saleMetrics?.metrics.detailedMetrics.periodDetails.map(detail => detail.label) || [],
      data: saleMetrics?.metrics.detailedMetrics.periodDetails.map(detail => detail.soldUnits) || [],
    },
    week: {
      labels: saleMetrics?.metrics.detailedMetrics.periodDetails.map(detail => detail.label) || [],
      data: saleMetrics?.metrics.detailedMetrics.periodDetails.map(detail => detail.soldUnits) || [],
    }
  };

  const months = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  // Função para determinar a cor do velocímetro baseado na porcentagem
  const getSpeedometerColor = (percentage: number) => {
    if (percentage <= 30) return '#FF4B4B'; // Vermelho
    if (percentage <= 50) return '#FFD700'; // Amarelo
    return '#4CAF50'; // Verde
  };

  // Dados simulados do VGV por mês
  const vgvData: Record<number, number> = {
    0: 25, // Janeiro
    1: 35, // Fevereiro
    2: 45, // Março
    3: 55, // Abril
    4: 62, // Maio
    5: 28, // Junho
    6: 42, // Julho
    7: 48, // Agosto
    8: 52, // Setembro
    9: 65, // Outubro
    10: 38, // Novembro
    11: 58, // Dezembro
  };

  // Atualiza o VGV quando o mês é alterado
  useEffect(() => {
    setVgvPercentage(vgvData[selectedMonth]);
  }, [selectedMonth]);

  // Configuração do gráfico velocímetro com gradiente
  const speedometerData = {
    labels: ['Progresso', 'Restante'],
    datasets: [{
      data: [vgvPercentage, 100 - vgvPercentage],
      backgroundColor: [
        getSpeedometerColor(vgvPercentage),
        '#E0E0E0'
      ],
      borderWidth: 0,
      circumference: 180,
      rotation: 270,
    }]
  };

  const speedometerOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false
      }
    },
    cutout: '70%'
  };

  useEffect(() => {
    const loadEnterprises = async () => {
      try {
        const enterpriseProvider = EnterpriseProvider.getInstance();
        const response = await enterpriseProvider.getEnterprisesName();
        setEnterprises(response);
      } catch (error) {
        console.error('Erro ao carregar empreendimentos:', error);
      }
    };

    loadEnterprises();
  }, []);

  useEffect(() => {
    const loadMetrics = async () => {
      try {
        const metricsProvider = MetricsProvider.getInstance();
        const params: any = {
          periodType: selectedPeriod
        };

        if (selectedEnterprise !== 'all') {
          params.enterpriseId = parseInt(selectedEnterprise);
        }

        const response = await metricsProvider.getSaleMetrics(params);
        setSaleMetrics(response);

        // Atualizar métricas com os dados da API
        if (response.metrics) {
          setMetrics({
            vendas: response.metrics.detailedMetrics.vgv.totalSoldUnits,
            empreendimentos: response.metrics.enterprisesCount || 0,
            corretores: response.metrics.correspondents,
            visitas: response.metrics.scheduledVisits.total,
            leads: response.metrics.leads,
            bouncerate: 0, // Não disponível na API
            valor: Math.round(response.metrics.detailedMetrics.vgv.totalSoldValue / 1000) // Convertendo para milhares
          });

          setVgvPercentage(response.metrics.detailedMetrics.vgv.totalSoldPercentage);
        }
      } catch (error) {
        console.error('Erro ao carregar métricas:', error);
      }
    };

    loadMetrics();
  }, [selectedPeriod, selectedEnterprise]);

  const monthlyData = {
    labels: saleMetrics?.metrics.detailedMetrics.periodDetails.map(detail => detail.label) || [],
    datasets: [{
      label: 'Desempenho',
      data: saleMetrics?.metrics.detailedMetrics.periodDetails.map(detail => detail.soldValue) || [],
      fill: false,
      borderColor: '#192158',
      tension: 0.4
    }]
  };

  const categoryData = {
    labels: ['Residencial Vertical', 'Residencial Horizontal', 'Comercial', 'Industrial', 'Rural', 'Terrenos'],
    datasets: [{
      data: [30, 25, 20, 15, 5, 5],
      backgroundColor: [
        '#192158',
        '#2A3E7B',
        '#3A4D9E',
        '#4A5DBF',
        '#5A6DDF',
        '#10163A',
      ]
    }]
  };

  const channelData = {
    labels: ['Pendente', 'Aceito', 'Em analise', 'Rejeitada'],
    datasets: [{
      label: 'Desempenho por Canal',
      data: [65, 85, 55, 45],
      backgroundColor: '#192158',
    }]
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="enterprise-selector">
          <select
            value={selectedEnterprise}
            onChange={(e) => setSelectedEnterprise(e.target.value)}
            className="enterprise-select"
          >
            <option value="all">Todos</option>
            {enterprises.map((enterprise) => (
              <option key={enterprise.id} value={enterprise.id}>
                {enterprise.name}
              </option>
            ))}
          </select>
        </div>
        <div className="period-selector">
          <button
            className={selectedPeriod === 'year' ? 'active' : ''}
            onClick={() => setSelectedPeriod('year')}
          >
            Ano
          </button>
          <button
            className={selectedPeriod === 'month' ? 'active' : ''}
            onClick={() => setSelectedPeriod('month')}
          >
            Mês
          </button>
          <button
            className={selectedPeriod === 'week' ? 'active' : ''}
            onClick={() => setSelectedPeriod('week')}
          >
            Semana
          </button>
        </div>
      </header>

      {selectedEnterprise === 'all' ? (
        <>
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon sales">
                <DollarSign size={24} />
              </div>
              <div className="metric-content">
                <h3>{metrics.vendas}</h3>
                <p>Vendas mensais</p>
                <span className="trend positive">+9,21%</span>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon projects">
                <Users size={24} />
              </div>
              <div className="metric-content">
                <h3>{metrics.empreendimentos}</h3>
                <p>Empreendimentos</p>
                <span className="trend positive">+2,43%</span>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon agents">
                <TrendingUp size={24} />
              </div>
              <div className="metric-content">
                <h3>{metrics.corretores}</h3>
                <p>Produção dos corretores</p>
                <span className="trend positive">+3,23%</span>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon leads">
                <Eye size={24} />
              </div>
              <div className="metric-content">
                <h3>{metrics.leads}</h3>
                <p>Leads</p>
                <span className="trend positive">+9,22%</span>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon visits">
                <Target size={24} />
              </div>
              <div className="metric-content">
                <h3>{metrics.visitas}</h3>
                <p>Visitas</p>
                <span className="trend negative">-3,21%</span>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon revenue">
                <Wallet size={24} />
              </div>
              <div className="metric-content">
                <h3 className="metric-value">R$ {new Intl.NumberFormat('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(metrics.valor)}</h3>
                <p>Valor de vendas</p>
                <span className="trend positive">+9,34%</span>
              </div>
            </div>
          </div>

          <div className="charts-grid">
            <div className="chart-card">
              <h3>Distribuição por categoria</h3>
              <Doughnut data={categoryData} options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'right' as const,
                    align: 'center' as const,
                    labels: {
                      boxWidth: 15,
                      padding: 15,
                      font: {
                        size: 12
                      }
                    }
                  }
                },
                layout: {
                  padding: {
                    right: 50
                  }
                }
              }} />
            </div>

            <div className="chart-card">
              <h3>Status das propostas</h3>
              <Bar data={channelData} options={{
                indexAxis: 'y' as const,
                responsive: true,
                plugins: {
                  legend: {
                    display: false
                  }
                }
              }} />
            </div>

            <div className="chart-card full-width">
              <h3>Valor de vendas (R$)</h3>
              <Line data={monthlyData} options={{
                responsive: true,
                plugins: {
                  legend: {
                    display: false
                  }
                }
              }} />
            </div>

            <div className="chart-card full-width">
              <h3>Quantidade de Vendas (unidades)</h3>
              <Line data={{
                labels: saleMetrics?.metrics.detailedMetrics.periodDetails.map(detail => detail.label) || [],
                datasets: [{
                  label: 'Quantidade',
                  data: saleMetrics?.metrics.detailedMetrics.periodDetails.map(detail => detail.soldUnits) || [],
                  fill: false,
                  borderColor: '#192158',
                  tension: 0.4
                }]
              }} options={{
                responsive: true,
                plugins: {
                  legend: {
                    display: false
                  }
                }
              }} />
            </div>
          </div>
        </>
      ) : (
        <div className="enterprise-view">
          <div className="speedometer-container">
            <div className="speedometer-header">
              <h3>VGV - Valor Geral de Venda</h3>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="month-select"
              >
                {months.map((month, index) => (
                  <option key={index} value={index}>{month}</option>
                ))}
              </select>
            </div>
            <div className="speedometer-card">
              <div className="speedometer-wrapper">
                <Doughnut data={speedometerData} options={speedometerOptions} />
                <div className="speedometer-value">
                  <h2>{vgvPercentage}%</h2>
                  <p>Meta: 30%</p>
                </div>
                <div className="speedometer-info">
                  <div className="info-item">
                    <span className="dot red"></span>
                    <p>0% - 30%</p>
                  </div>
                  <div className="info-item">
                    <span className="dot yellow"></span>
                    <p>31% - 50%</p>
                  </div>
                  <div className="info-item">
                    <span className="dot green"></span>
                    <p>51% - 100%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="charts-grid">
            <div className="chart-card">
              <h3>Valor de vendas (R$)</h3>
              <Line data={{
                labels: salesValueData[selectedPeriod].labels,
                datasets: [{
                  label: 'Valor em R$',
                  data: salesValueData[selectedPeriod].data,
                  fill: false,
                  borderColor: '#192158',
                  tension: 0.4
                }]
              }} options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true
                  }
                }
              }} />
            </div>

            <div className="chart-card">
              <h3>Quantidade de Vendas (unidades)</h3>
              <Line data={{
                labels: salesQuantityData[selectedPeriod].labels,
                datasets: [{
                  label: 'Quantidade',
                  data: salesQuantityData[selectedPeriod].data,
                  fill: false,
                  borderColor: '#192158',
                  tension: 0.4
                }]
              }} options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true
                  }
                }
              }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;